import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { powerAtom } from '../atoms/power';
import { CircularProgress } from '@mui/material';
import { BuildingIDProps } from '../atoms/chargers';
import { DateTime } from 'luxon';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel, toEasternDateTime } from '../converters/timespanConverter';

interface VoltageGraphProps extends BuildingIDProps {
  startDateTime: DateTime;
  endDateTime: DateTime;
  isLoading?: boolean;
  forceNoDataOnEmpty?: boolean;
}

const VoltageGraph: React.FC<VoltageGraphProps> = ({ BuildingID, startDateTime, endDateTime, isLoading, forceNoDataOnEmpty }) => {
  const [powerDataMap] = useAtom(powerAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({
    VA: true, VB: true, VC: true,
  });
  const chartRef = useRef<ReactECharts>(null);

  const powerData = useMemo(
    () => (
      BuildingID && Array.isArray(powerDataMap[BuildingID.toLowerCase()])
        ? powerDataMap[BuildingID.toLowerCase()]
        : []
    ),
    [BuildingID, powerDataMap]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      if (chartRef.current) chartRef.current.getEchartsInstance().resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (powerData && powerData.length > 0 && chartRef.current) {
      const timer = setTimeout(() => {
        if (chartRef.current) chartRef.current.getEchartsInstance().resize();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [powerData]);

  useEffect(() => {
    if (!chartRef.current) return;
    const echartsInstance = chartRef.current.getEchartsInstance();
    if (!powerData || powerData.length === 0) {
      echartsInstance.clear();
      return;
    }
    const vA = powerData.map((b) => b.VA ?? 0);
    const vB = powerData.map((b) => b.VB ?? 0);
    const vC = powerData.map((b) => b.VC ?? 0);
    echartsInstance.setOption(
      {
        series: [{ data: vA }, { data: vB }, { data: vC }],
        legend: { selected: legendSelected },
      },
      false
    );
    setTimeout(() => echartsInstance.resize(), 0);
  }, [powerData, legendSelected]);

  if (!powerData) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
        <CircularProgress style={{ color: 'steelblue' }} />
        <h3 style={{ color: 'text.primary', marginTop: '20px' }}>Waiting for data...</h3>
      </div>
    );
  }

  if ((forceNoDataOnEmpty || !isLoading) && Array.isArray(powerData) && powerData.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
        <h3 style={{ color: 'grey' }}>No voltage data for selected range</h3>
      </div>
    );
  }

  const xAxisData: string[] = [];
  const verticalLines: any[] = [];
  let previousDate = '';
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);

  powerData.forEach((b, index) => {
    const timestamp = toEasternDateTime(b);
    const formattedTime = formatTimeLabel(timestamp, showHalfHours);
    const currentDate = timestamp.toFormat('M/d');
    xAxisData.push(formattedTime);
    if (currentDate !== previousDate && previousDate !== '') {
      verticalLines.push({
        xAxis: index,
        lineStyle: { color: 'lightgrey', type: 'dashed' },
        label: { formatter: currentDate, position: 'end', fontSize: 10, color: 'grey' },
        tooltip: { formatter: `Date: ${currentDate}` },
      });
    }
    previousDate = currentDate;
  });

  const vA = powerData.map((b) => b.VA ?? 0);
  const vB = powerData.map((b) => b.VB ?? 0);
  const vC = powerData.map((b) => b.VC ?? 0);

  const option = {
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
    title: {
      text: 'Line Voltages',
      textStyle: { color: 'grey', fontSize: 14 },
      left: 0,
      top: 0,
    },
    tooltip: {
      show: !isMobile,
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: (params: any) => {
        const timestamp = toEasternDateTime(powerData[params[0].dataIndex]);
        const formattedTime = timestamp.toFormat('MM/dd/yyyy h:mm:ss a');
        let result = `${formattedTime}<br/>`;
        params.forEach((param: any) => {
          const value = param.value === null || param.value === undefined ? 'No data' : Number(param.value).toFixed(2);
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>${param.seriesName}: ${value} V<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: ['VA', 'VB', 'VC'],
      textStyle: { color: 'grey' },
      bottom: 0,
      padding: [5, 0, 0, 0],
      selected: legendSelected,
      selector: [
        { type: 'all', title: 'View All' },
        { type: 'inverse', title: 'All Off' },
      ],
      selectorPosition: 'end',
      selectorLabel: { color: 'grey' },
    },
    grid: { left: '3%', right: '4%', bottom: '14%', top: '12%', containLabel: true },
    toolbox: { feature: { saveAsImage: {} } },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: xAxisData,
      axisLabel: {
        color: 'grey',
        rotate: 45,
        interval: (index: number) => {
          const timestamp = toEasternDateTime(powerData[index]);
          return shouldShowTimeLabel(timestamp, startDateTime, interval, showHalfHours);
        },
        formatter: (value: string) => {
          const timestamp = toEasternDateTime(powerData[xAxisData.indexOf(value)]);
          return formatTimeLabel(timestamp, showHalfHours);
        },
      },
    },
    yAxis: { type: 'value', name: 'Volts', axisLabel: { color: 'grey' } },
    series: [
      { name: 'VA', type: 'line', data: vA, lineStyle: { color: 'royalblue' }, itemStyle: { color: 'royalblue' },
        markLine: { symbol: 'none', data: verticalLines } },
      { name: 'VB', type: 'line', data: vB, lineStyle: { color: 'darkorange' }, itemStyle: { color: 'darkorange' } },
      { name: 'VC', type: 'line', data: vC, lineStyle: { color: 'seagreen' }, itemStyle: { color: 'seagreen' } },
    ],
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', minHeight: '360px' }}>
      <ReactECharts
        ref={chartRef}
        option={option}
        className="cancelSelectorName"
        style={{ height: '100%', width: '100%', minHeight: '360px' }}
        opts={{ renderer: 'canvas' }}
        onEvents={{
          legendselectchanged: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
          legendinverseselect: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
          legendselectall: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
        }}
        notMerge={false}
      />
    </div>
  );
};

export default VoltageGraph;
